import { styled } from "@linaria/react"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"


import { Badge } from "@/components/Badge/Badge.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { ConfirmDialog, Dialog } from "@/components/Dialog/Dialog.tsx"
import { DropZone } from "@/components/DropZone/DropZone.tsx"
import { DropZoneModel } from "@/components/DropZone/DropZoneModel.ts"
import { Gap, Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { FullWidthInput } from "@/components/Input/Input.tsx"
import { InputLikeTextarea } from "@/components/ResizeableTextarea/ResizeableTextarea.tsx"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { DeleteLight } from "@/icons/Icons.ts"
import { formatDate, formatDateTime } from "@/lib/date.ts"
import { formatErrorMessage } from "@/lib/displayUtils.ts"
import { looksLikeBinary } from "@/lib/std.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ButtonKind } from "@/components/Button/Button.tsx"
import type { ProfileIdentifier, SshKeyData } from "@/lib/client/generated/TypeDefinitions.ts"
import type { SshKeysTabModel } from "@/routes/user/SshKeysTabModel.ts"

export const SshKeysTabContent = observer(({ model, editable }: {
  model: SshKeysTabModel
  editable: boolean
}) => {
  const [keyToDelete, setKeyToDelete] = useState<SshKeyData | null>(null)

  if (model.isLoading.get()) {
    return (
      <Spinner text={ellipsize("Loading keys")} />
    )
  } else {
    if (model.keys.get()?.length === 0) {
      return (
        <Vertical gap="16px">
          <SecondaryTextSpan>No SSH keys have been added yet</SecondaryTextSpan>
          {editable && <SshKeysTabActions model={model} profile={model.profile} addKeyButtonKind="alternative" />}
        </Vertical>
      )
    }

    return (
      <Container>
        {editable && <SshKeysTabActions model={model} profile={model.profile} addKeyButtonKind="alternative" />}

        <Gap />

        {model.keys.get()?.map((it) => {
          return (
            <KeyItem key={it.fingerprint}>
              <Horizontal alignItems="center" gap="8px">
                <Badge kind="info">Public</Badge>
                <Spring />
                {editable && (
                  <Tooltip content="Delete key">
                    <Button kind="icon" onClick={() => {
                      setKeyToDelete(it)
                    }}><DeleteLight /></Button>
                  </Tooltip>
                )}
              </Horizontal>
              <KeyItemSection>
                <KeyItemTitle>Fingerprint</KeyItemTitle>
                <KeyFingerprint>{it.fingerprint}</KeyFingerprint>
                {it.sha256fingerprint && <KeyFingerprint>{it.sha256fingerprint}</KeyFingerprint>}
              </KeyItemSection>
              <KeyItemSection>
                <KeyItemTitle>Comment</KeyItemTitle>
                <span>{it.comment}</span>
              </KeyItemSection>
              <Horizontal alignItems="center" gap="8px">
                {it.createdAt && (
                  <Tooltip content={`Key added: ${formatDateTime(it.createdAt)}`}>
                    <CreatedAt>{formatDate(it.createdAt)}</CreatedAt>
                  </Tooltip>
                )}
                <Spring />
                {it.lastUsed && (
                  <Tooltip content={`Last used: ${formatDateTime(it.lastUsed)} (${it.lastResource || "unknown"})`}>
                    <LastUsed>Last used: {formatDateTime(it.lastUsed)}
                    </LastUsed>
                  </Tooltip>
                )}
              </Horizontal>
            </KeyItem>
          )
        })}

        <ConfirmDialog
          open={keyToDelete != null}
          onOpenChange={(open) => {
            if (!open) setKeyToDelete(null)
          }}
          trigger={null}
          description="Are you sure you want to delete this key?"
          confirmText="Delete"
          onConfirm={async () => {
            if (keyToDelete != null) {
              await model.deleteKey(model.profile, keyToDelete.fingerprint)
              setKeyToDelete(null)
            }
          }}
        />
      </Container>
    )
  }
})

export const SshKeysTabActions = ({ model, profile, addKeyButtonKind }: {
  model: SshKeysTabModel
  profile: ProfileIdentifier.Username
  addKeyButtonKind: ButtonKind
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <ActionsContainer>
      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        trigger={<AddKeyButton kind={addKeyButtonKind} onClick={() => setDialogOpen(true)}>Add key</AddKeyButton>}
        title="Add SSH Key"
      >
        <AddKeyForm model={model} profile={profile} setOpen={setDialogOpen} />
      </Dialog>
    </ActionsContainer>
  )
}

const AddKeyForm = observer(({ model, profile, setOpen }: {
  model: SshKeysTabModel
  profile: ProfileIdentifier.Username
  setOpen: (open: boolean) => void
}) => {
  const publicKeyRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    setTimeout(() => publicKeyRef.current?.focus(), 0)
  }, [])

  const dropZoneModel = useModel(() => new DropZoneModel({
    onFileSelected: (file) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const value = e.target?.result

        if (typeof value === "string" && !looksLikeBinary(value)) {
          runInAction(() => {
            model.publicKey.set(value)
          })
        } else {
          toast.error("Failed to read the public key file")
        }
      }

      reader.onerror = () => {
        toast.error("Failed to read the public key file")
      }

      reader.readAsText(file)
    }
  }))

  return (
    <FormContainer>
      <InputLikeTextarea
        ref={publicKeyRef}
        value={model.publicKey.get()}
        onChange={(e) => runInAction(() => model.publicKey.set(e.target.value))}
        onDragOver={(e) => {
          e.preventDefault()
          e.dataTransfer.dropEffect = "none"
        }}
        onDrop={(e) => e.preventDefault()}
        placeholder="Public key"
        maxHeight="50vh"
      />

      {model.publicKey.get().length === 0 ? (
        <DropZone model={dropZoneModel} placeholder="Click to browse or drag your public key here" />
      ) : (
        <Horizontal alignItems="center" gap="8px">
          Public key is loaded
          <Button kind="alternative" onClick={() => runInAction(() => model.publicKey.set(""))}>Clear</Button>
        </Horizontal>
      )}

      <FullWidthInput
        value={model.comment.get()}
        onChange={(e) => runInAction(() => model.comment.set(e.target.value))}
        placeholder="Comment"
      />

      {model.error.get() && (
        <FormErrorMessage>{formatErrorMessage(model.error.get(), "Unknown error")}</FormErrorMessage>
      )}

      <FormSubmitContainer>
        <Spring />

        <Button kind="alternative" onClick={() => {
          setOpen(false)
        }}>Cancel</Button>

        <Button kind="primary" disabled={model.publicKey.get().trim().length === 0} onClick={() => {
          model.addKey(profile, model.publicKey.get(), model.comment.get()).then(() => {
            runInAction(() => {
              model.publicKey.set("")
              model.comment.set("")
              model.error.set(null)
            })
            setOpen(false)
          }).catch((error) => {
            runInAction(() => model.error.set(error))
          })
        }}>Add key</Button>
      </FormSubmitContainer>
    </FormContainer>
  )
})

const KeyItem = styled(Vertical)`
  gap: 12px;
  padding: 12px;
  background: var(--main-color-5);
  border-radius: var(--border-radius-8);
`

const KeyItemSection = styled(Vertical)`
`

const KeyItemTitle = styled.span`
  color: var(--secondary-text-color);

  ${textStyles.smallerFontSize};
`

const CreatedAt = styled.span`
  color: var(--secondary-text-color);

  ${textStyles.smallerFontSize};
`

const LastUsed = styled.span`
  color: var(--secondary-text-color);

  ${textStyles.smallerFontSize};
`

const KeyFingerprint = styled.pre`
  ${textStyles.pre};
`

const ActionsContainer = styled(Vertical)`
  align-items: flex-start;
`

const AddKeyButton = styled(Button)`
`

const FormContainer = styled(Vertical)`
  gap: 16px;
  width: 100%;
`

const FormSubmitContainer = styled(Horizontal)`
  align-items: center;
  gap: 8px;
`

const FormErrorMessage = styled.div`
  color: var(--error-color);
`

const Container = styled(Vertical)`
  gap: 8px;
`
